import type { InvoiceChannel, KitchenBoardPlate } from "@/lib/api/types";

/**
 * WHOSE BAG IS THIS (T13, D170) — the off-premise card's identity line.
 *
 * Takeaway and delivery are marked by a WORD and a SHAPE, never by colour
 * alone; then `platform · ref · first name` (the server computed the first
 * name — a staff meal's name whole — and never sends a phone, an address or a
 * surname); then the order's TOTAL age since it was first fired. Pure.
 */

export function isOffPremise(channel: InvoiceChannel): boolean {
  return channel === "TAKEAWAY" || channel === "DELIVERY";
}

/** The Lucide shape beside the channel word: a bag for takeaway, a bike for delivery. */
export function channelShape(channel: InvoiceChannel): "bag" | "bike" | null {
  switch (channel) {
    case "TAKEAWAY":
      return "bag";
    case "DELIVERY":
      return "bike";
    case "DINE_IN":
      return null;
  }
}

/** `platform · ref · first name`, the parts that exist, or null when none does. */
export function identityLine(
  plate: Pick<KitchenBoardPlate, "platformName" | "platformOrderRef" | "customerFirstName">,
): string | null {
  const parts = [plate.platformName, plate.platformOrderRef, plate.customerFirstName]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** When the guest's order first went to the kitchen: the first fire (T9), else this fire. */
export function firstFireOf(plate: Pick<KitchenBoardPlate, "firstFiredAt" | "firedAt">): number | null {
  const since = plate.firstFiredAt ?? plate.firedAt;
  if (!since) return null;
  const parsed = new Date(since).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * «منذ الطلب N د» — the guest's real wait, from the EARLIEST first fire among
 * `plates`, in whole minutes; null when no plate has a fire time.
 */
export function totalAgeMinutes(
  plates: readonly Pick<KitchenBoardPlate, "firstFiredAt" | "firedAt">[],
  now: number,
): number | null {
  let earliest: number | null = null;
  for (const plate of plates) {
    const at = firstFireOf(plate);
    if (at !== null && (earliest === null || at < earliest)) earliest = at;
  }
  return earliest === null ? null : Math.max(0, Math.floor((now - earliest) / 60_000));
}

/** Whether a card shows its total age: every off-premise card and every remade one. */
export function showsTotalAge(
  plates: readonly Pick<KitchenBoardPlate, "channel" | "remakeCount">[],
): boolean {
  return plates.some((plate) => isOffPremise(plate.channel) || plate.remakeCount > 0);
}
